import type { ComponentChildren, RefObject } from "preact";
import { createPortal } from "preact/compat";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import { Icon, type IconName } from "../ui/icon";

// The sheet's own width below which the panel is a bottom sheet; the width where `.cs-pair` stacks.
const NARROW = 720;

// Follows the sheet's width, not the window's, so the same sheet is right-docked on a page and a bottom sheet in a small panel.
export function useNarrow(root: RefObject<HTMLElement>, below = NARROW): boolean {
	const [narrow, setNarrow] = useState(false);
	useEffect(() => {
		const element = root.current;
		if (!element || typeof ResizeObserver === "undefined") return undefined;
		const measure = () => setNarrow(element.getBoundingClientRect().width < below);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [root, below]);
	return narrow;
}

// What the sheet in Mapas' panel says to the board, which owns the toasts, the roll cards and the panel's close button.
export function tellBoard(root: HTMLElement | null, name: string, detail: object = {}) {
	root?.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

// Where Mapas' panel keeps its popups, inside the panel; the page's popups go to the body.
export const popupHost = (root: HTMLElement | null) => root?.closest("[data-sheet-panel]")?.querySelector<HTMLElement>("[data-sheet-popups]") ?? null;

// A link in the sheet must not take the board's tab, which never refreshes: in the panel they open a new one.
export function useBlankLinks(on: boolean) {
	useEffect(() => {
		if (!on) return undefined;
		const open = (event: MouseEvent) => {
			const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
			if (!(link instanceof HTMLAnchorElement) || link.getAttribute("href")?.startsWith("#") || !link.closest(".cs-root, .cs-popup")) return;
			link.target = "_blank";
			link.rel = "noopener";
		};
		document.addEventListener("click", open, true);
		return () => document.removeEventListener("click", open, true);
	}, [on]);
}

// A panel covered by a modal ignores Escape: an `inert` ancestor, or an open native modal dialog it is not inside (that
// one makes the page inert without the attribute). A key another handler already took is spent.
export const ownsEscape = (event: KeyboardEvent, panel: HTMLElement | null, doc: Pick<Document, "querySelector"> = document) => {
	if (event.key !== "Escape" || event.defaultPrevented || panel?.closest("[inert]")) return false;
	const dialog = doc.querySelector("dialog:modal");
	return !dialog || !!dialog.contains(panel);
};

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select, textarea";

function keepFocusInside(event: KeyboardEvent, panel: HTMLElement) {
	const inside = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
	const first = inside[0];
	const last = inside[inside.length - 1];
	if (!first || !last) return;
	if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
		event.preventDefault();
		last.focus();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus();
	}
}

// One popup over the sheet: a panel docked right, or a bottom sheet when the sheet is narrow. It is a portal, because
// the sheet's container would make a fixed panel position against the sheet instead of the window.
export function Panel({
	title,
	icon,
	iconClass = "text-carmesim-600",
	aside,
	wide = false,
	tall = false,
	modal = true,
	bare = false,
	closeLabel,
	narrow,
	host,
	opener,
	onClose,
	children,
}: {
	title: string;
	icon: IconName;
	iconClass?: string;
	// Beside the title, before the close button: the purse, in the catalog's.
	aside?: ComponentChildren;
	// Docked right, a wider panel, for a popup that is a list.
	wide?: boolean;
	// Docked right, the site's drawer: the window's whole height, and a sheet that tall on a phone.
	tall?: boolean;
	// False for a panel to read beside the page: no backdrop, nothing inert, no focus trap; Escape and the button still close it.
	modal?: boolean;
	// The children draw their own header and close button, and fill the panel.
	bare?: boolean;
	closeLabel: string;
	narrow: boolean;
	// Mapas' panel, when the popup opens inside it: the board behind stays live, so nothing is made inert.
	host?: HTMLElement | null;
	// Where focus goes back to when the panel closes.
	opener: HTMLElement | null;
	onClose: () => void;
	children: ComponentChildren;
}) {
	const panel = useRef<HTMLDivElement>(null);
	const close = useRef(onClose);
	close.current = onClose;
	const heading = useId();

	// Layout, not passive: Mapas' Escape stands aside once the dialog is drawn, so the listener must already be there.
	useLayoutEffect(() => {
		const element = panel.current;
		// The rest of the page is inert while the panel is open, so neither Tab nor a page shortcut can reach it.
		const behind =
			host || !modal
				? []
				: [...document.body.children].filter((child): child is HTMLElement => child instanceof HTMLElement && !child.contains(element) && !child.inert);
		for (const child of behind) child.inert = true;
		// The sheet slides in from below the panel, so a scrolling focus would carry the panel itself up with it.
		element?.focus({ preventScroll: true });
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				if (!ownsEscape(event, element)) return;
				event.preventDefault();
				// The board's own Escape would otherwise deselect behind the popup.
				if (host) event.stopPropagation();
				close.current();
			} else if (modal && event.key === "Tab" && element) {
				keepFocusInside(event, element);
			}
		};
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("keydown", onKey);
			for (const child of behind) child.inert = false;
			opener?.focus();
		};
	}, [opener, host, modal]);

	return createPortal(
		<div
			class="cs-popup"
			data-dock={narrow ? "bottom" : "right"}
			data-wide={wide ? "" : undefined}
			data-tall={tall ? "" : undefined}
			data-modal={modal ? undefined : "false"}
			data-host={host ? "panel" : undefined}
		>
			{modal && <div class="cs-backdrop" aria-hidden="true" onClick={() => onClose()} />}
			<div
				ref={panel}
				role="dialog"
				aria-modal={modal}
				aria-label={bare ? title : undefined}
				aria-labelledby={bare ? undefined : heading}
				tabIndex={-1}
				class={`cs-panel odo-board-panel ${bare ? "bg-surface-raised" : ""}`}
			>
				{!bare && (
					<div class="flex flex-wrap items-center gap-x-2 border-b border-line bg-surface-raised px-3 py-1">
						<Icon name={icon} class={`h-5 w-5 ${iconClass}`} />
						<h2 id={heading} class="cs-title min-w-0 flex-1 text-lg font-bold">
							{title}
						</h2>
						{aside && <div class={narrow ? "order-last flex basis-full justify-end pb-1.5" : "flex"}>{aside}</div>}
						<button
							type="button"
							aria-label={closeLabel}
							class="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg text-ink hover:bg-surface-sunken"
							onClick={() => onClose()}
						>
							<Icon name="close" class="h-3.5 w-3.5" />
						</button>
					</div>
				)}
				<div class={bare ? "flex min-h-0 flex-1 flex-col" : "min-h-0 flex-1 overflow-y-auto overscroll-contain px-3.5 py-3"}>{children}</div>
			</div>
		</div>,
		host ?? document.body,
	);
}
